// @flow strict

import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import debounce from 'lodash.debounce';
import { v4 as uuid } from 'uuid';
import {
  Autocomplete,
  AutocompleteItem,
  FormGrid,
  FormGridCell,
  FormGridRow,
} from '@ansarada/ace-react';

import type { Location } from '../types';
import { PROFILE_FORM_KEYS as KEYS } from '../constants';
import { profile } from '../api';

export type LocationInputProps = {
  isShowError: (field: string) => boolean,
  disabled: boolean,
  onBlur?: (location: ?Location) => void,
};

export default function LocationInput({ isShowError, onBlur, disabled }: LocationInputProps) {
  const { setFieldValue, errors } = useFormikContext();
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [selectedItem, setSelectedItem] = useState<?AutocompleteItem>();
  const [autoCompleteKey, setAutoCompleteKey] = useState(uuid());

  useEffect(() => {
    if (locationSearchValue) {
      setIsLocationLoading(true);
      profile
        .searchLocation(locationSearchValue)
        .then(newLocations => setLocations(newLocations.locations || []))
        // eslint-disable-next-line no-console
        .catch(locationError => console.error(locationError))
        .finally(() => setIsLocationLoading(false));
    }
  }, [locationSearchValue]);

  const createLocationAutocompleteItems = (): Array<AutocompleteItem> => {
    return locations.map(l => new AutocompleteItem(String(l.locationId), l.locationName));
  };

  const onSelectedLocationChange = (item: AutocompleteItem) => {
    const selectedLocation = locations.find(l => l.locationId === +item.value);
    setSelectedItem(item);
    setFieldValue(KEYS.locationId, (selectedLocation && String(selectedLocation.locationId)) || '');
  };

  const onLocationSearchChange = debounce((value: string, selected: boolean) => {
    setLocations([]);
    if (!selected) {
      setLocationSearchValue(value);
    }
  }, 300);

  const onComponentLeaveFocus = () => {
    // clear out current location list otherwise when the use re-focus the previous search locations
    setLocations([]);

    // force React render new AutoComplete component
    setAutoCompleteKey(uuid());

    if (onBlur) {
      onBlur(
        selectedItem
          ? {
              locationId: parseInt(selectedItem.value, 10),
              locationName: selectedItem._label,
            }
          : null,
      );
    }
  };

  return (
    <FormGrid>
      <FormGridRow>
        <FormGridCell
          labelText="City"
          errors={isShowError(KEYS.locationId) ? <span>{errors.locationId}</span> : undefined}
          errorPosition="Bottom"
        >
          <div onBlur={onComponentLeaveFocus}>
            <Autocomplete
              key={autoCompleteKey}
              data-test-id="location"
              placeholder="Enter your location or closest city"
              defaultValue={selectedItem ? selectedItem._label : ''}
              selected={selectedItem}
              items={createLocationAutocompleteItems()}
              loading={isLocationLoading}
              onSelected={onSelectedLocationChange}
              onChangeValue={onLocationSearchChange}
              status={isShowError(KEYS.locationId) ? 'Bad' : undefined}
              disabled={disabled}
            />
          </div>
        </FormGridCell>
      </FormGridRow>
    </FormGrid>
  );
}
